package com.partejar.app.core.ui.components

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.animation.togetherWith
import androidx.compose.animation.core.snap
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyItemScope
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import com.partejar.app.core.ui.theme.PartejarTheme

enum class PartejarListPhase {
    LOADING,
    EMPTY,
    ERROR,
    CONTENT
}

/**
 * Shared list behavior and structure. Item and state content remain owned by the feature.
 */
@Composable
fun <T> PartejarList(
    items: List<T>,
    key: (T) -> Any,
    phase: PartejarListPhase,
    emptyContent: @Composable BoxScope.() -> Unit,
    errorContent: @Composable BoxScope.() -> Unit,
    modifier: Modifier = Modifier,
    bottomClearance: Dp? = null,
    isRefreshing: Boolean = false,
    hasNextPage: Boolean = false,
    isLoadingNextPage: Boolean = false,
    hasNextPageError: Boolean = false,
    onLoadNextPage: (() -> Unit)? = null,
    loadingContent: @Composable BoxScope.() -> Unit = { PartejarListLoading() },
    refreshingContent: @Composable () -> Unit = { PartejarListLoading(Modifier.fillMaxWidth()) },
    nextPageLoadingContent: @Composable () -> Unit = { PartejarListLoading(Modifier.fillMaxWidth()) },
    nextPageErrorContent: @Composable () -> Unit = {},
    listState: LazyListState = rememberLazyListState(),
    itemContent: @Composable LazyItemScope.(T) -> Unit
) {
    val reduced = reducedMotion()
    val distance = with(LocalDensity.current) { PartejarTheme.spacing.space12.roundToPx() }
    val stateBottomClearance = bottomClearance
        ?: WindowInsets.safeDrawing.asPaddingValues().calculateBottomPadding()
    AnimatedContent(
        targetState = phase,
        modifier = modifier,
        label = "partejarListState",
        transitionSpec = {
            val enter = if (reduced) {
                fadeIn(tween(PartejarMotion.press, easing = PartejarMotion.enter))
            } else {
                fadeIn(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) +
                    slideInVertically(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) { distance }
            }
            val exit = if (reduced) {
                fadeOut(tween(PartejarMotion.press, easing = PartejarMotion.exit))
            } else {
                fadeOut(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) +
                    slideOutVertically(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) { -distance }
            }
            enter togetherWith exit
        }
    ) { target ->
        val stateModifier = Modifier.fillMaxSize().padding(bottom = stateBottomClearance)
        when (target) {
            PartejarListPhase.LOADING -> Box(stateModifier, contentAlignment = Alignment.Center, content = loadingContent)
            PartejarListPhase.EMPTY -> Box(stateModifier, contentAlignment = Alignment.Center, content = emptyContent)
            PartejarListPhase.ERROR -> Box(stateModifier, contentAlignment = Alignment.Center, content = errorContent)
            PartejarListPhase.CONTENT -> PartejarLazyList(
                items = items,
                key = key,
                listState = listState,
                bottomClearance = bottomClearance,
                isRefreshing = isRefreshing,
                hasNextPage = hasNextPage,
                isLoadingNextPage = isLoadingNextPage,
                hasNextPageError = hasNextPageError,
                onLoadNextPage = onLoadNextPage,
                reducedMotion = reduced,
                refreshingContent = refreshingContent,
                nextPageLoadingContent = nextPageLoadingContent,
                nextPageErrorContent = nextPageErrorContent,
                itemContent = itemContent
            )
        }
    }
}

@Composable
private fun <T> PartejarLazyList(
    items: List<T>,
    key: (T) -> Any,
    listState: LazyListState,
    bottomClearance: Dp?,
    isRefreshing: Boolean,
    hasNextPage: Boolean,
    isLoadingNextPage: Boolean,
    hasNextPageError: Boolean,
    onLoadNextPage: (() -> Unit)?,
    reducedMotion: Boolean,
    refreshingContent: @Composable () -> Unit,
    nextPageLoadingContent: @Composable () -> Unit,
    nextPageErrorContent: @Composable () -> Unit,
    itemContent: @Composable LazyItemScope.(T) -> Unit
) {
    val spacing = PartejarTheme.spacing
    val safeBottom = WindowInsets.safeDrawing.asPaddingValues().calculateBottomPadding()
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        state = listState,
        contentPadding = PaddingValues(
            start = spacing.space16,
            end = spacing.space16,
            top = spacing.space4,
            bottom = bottomClearance ?: (safeBottom + spacing.space16)
        ),
        verticalArrangement = androidx.compose.foundation.layout.Arrangement.spacedBy(spacing.space12)
    ) {
        if (isRefreshing) {
            item(key = "partejar-list-refreshing") { refreshingContent() }
        }
        itemsIndexed(items, key = { _, item -> key(item) }) { index, item ->
            if (index >= items.lastIndex - 2 && hasNextPage && onLoadNextPage != null) {
                LaunchedEffect(key(item), items.size) { onLoadNextPage() }
            }
            val motionModifier = if (reducedMotion) {
                Modifier.animateItem(fadeInSpec = snap(), placementSpec = snap(), fadeOutSpec = snap())
            } else {
                Modifier.animateItem(
                    fadeInSpec = tween(PartejarMotion.entrance, easing = PartejarMotion.enter),
                    placementSpec = tween(PartejarMotion.feedback, easing = PartejarMotion.calm),
                    fadeOutSpec = tween(PartejarMotion.departure, easing = PartejarMotion.exit)
                )
            }
            Box(motionModifier.fillMaxWidth()) { itemContent(item) }
        }
        if (isLoadingNextPage) {
            item(key = "partejar-list-loading-next") { nextPageLoadingContent() }
        }
        if (hasNextPageError) {
            item(key = "partejar-list-next-error") { nextPageErrorContent() }
        }
    }
}

@Composable
private fun PartejarListLoading(modifier: Modifier = Modifier) {
    Box(modifier.padding(PartejarTheme.spacing.space12), contentAlignment = Alignment.Center) {
        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
    }
}
